import { Check, ChevronRight } from 'lucide-react'
import { DropdownMenu as M } from 'radix-ui'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export const DropdownMenu = M.Root
export const DropdownMenuTrigger = M.Trigger
export const DropdownMenuGroup = M.Group
export const DropdownMenuSub = M.Sub
export const DropdownMenuRadioGroup = M.RadioGroup

const contentClass =
  'z-50 min-w-44 overflow-hidden rounded-lg border border-line bg-surface p-1 text-sm text-ink shadow-pop animate-in'
const itemClass = cn(
  'relative flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 outline-none select-none',
  'data-disabled:pointer-events-none data-disabled:opacity-45 data-highlighted:bg-surface-3',
  '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-3',
)

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  align = 'end',
  ...props
}: ComponentProps<typeof M.Content>) {
  return (
    <M.Portal>
      <M.Content
        sideOffset={sideOffset}
        align={align}
        collisionPadding={8}
        className={cn(contentClass, className)}
        {...props}
      />
    </M.Portal>
  )
}

export function DropdownMenuItem({
  className,
  destructive,
  shortcut,
  children,
  ...props
}: ComponentProps<typeof M.Item> & { destructive?: boolean; shortcut?: ReactNode }) {
  return (
    <M.Item
      className={cn(itemClass, destructive && 'text-danger [&_svg]:text-danger', className)}
      {...props}
    >
      {children}
      {shortcut && <span className="ml-auto pl-4 text-xs text-ink-3">{shortcut}</span>}
    </M.Item>
  )
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof M.CheckboxItem>) {
  return (
    <M.CheckboxItem className={cn(itemClass, 'pl-7', className)} {...props}>
      <M.ItemIndicator className="absolute left-2 flex">
        <Check className="text-brass!" />
      </M.ItemIndicator>
      {children}
    </M.CheckboxItem>
  )
}

export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof M.RadioItem>) {
  return (
    <M.RadioItem className={cn(itemClass, 'pl-7', className)} {...props}>
      <M.ItemIndicator className="absolute left-2 flex">
        <Check className="text-brass!" />
      </M.ItemIndicator>
      {children}
    </M.RadioItem>
  )
}

export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof M.Label>) {
  return (
    <M.Label
      className={cn('px-2 pt-1.5 pb-1 text-xs font-medium text-ink-3', className)}
      {...props}
    />
  )
}

export function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof M.Separator>) {
  return <M.Separator className={cn('-mx-1 my-1 h-px bg-line', className)} {...props} />
}

export function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: ComponentProps<typeof M.SubTrigger>) {
  return (
    <M.SubTrigger className={cn(itemClass, 'data-[state=open]:bg-surface-3', className)} {...props}>
      {children}
      <ChevronRight className="ml-auto" />
    </M.SubTrigger>
  )
}

export function DropdownMenuSubContent({
  className,
  ...props
}: ComponentProps<typeof M.SubContent>) {
  return (
    <M.Portal>
      <M.SubContent sideOffset={4} className={cn(contentClass, className)} {...props} />
    </M.Portal>
  )
}
